import { memo, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import Person from '@mui/icons-material/Person';
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { Image } from '@realadvisor/image';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLocale } from '../../../../../src/hooks/locale';
import type { Translate } from '../../../../../src/hooks/locale';
import { getCurrencyByCountryCode } from '../../../../../src/locale';
import { formatPrice } from '../../../../../src/utils/format-price';
import { NoPhotosPlaceholder } from '../../../../components/NoPhotoPlaceholder';
import type { ComparableListingsCmaListing } from '../../../comparable-listings/ComparableListings';
import { usePropertyTypes } from '../PropertyTypesProvider';

import type { ListingAutomaticType } from './ListingAutomatic';
import { initialListingComparableDetails } from './ListingComparableDetails';
import ListingStatusChip from './ListingStatusChip';

/**
 * Returns formatted property characteristics string based on available data
 */
const getPropertyCharacteristics = (
  listing: Listing,
  countryCode: string,
  t: Translate,
): string => {
  const characteristics = [];

  // For Spain, prefer bedrooms count if available (on comparable listings)
  if (
    countryCode === 'ES' &&
    listing.number_of_bedrooms &&
    listing.number_of_bedrooms > 0
  ) {
    // For Spain, use "bedrooms" or fall back to "rooms" terminology if translation issue
    characteristics.push(
      t('bedroomsCount', { count: Number(listing.number_of_bedrooms) }),
    );
  } else if (listing.number_of_rooms) {
    characteristics.push(
      t('roomsCount', { count: Number(listing.number_of_rooms) }),
    );
  }

  // Only add surface if it's greater than 0
  if (listing.computed_surface && listing.computed_surface > 0) {
    characteristics.push(`${listing.computed_surface} m²`);
  }

  // Only add land surface if it's greater than 0
  if (listing.land_surface && listing?.land_surface > 0) {
    characteristics.push(
      t('plotSurfaceOf', { plotSurface: listing.land_surface }),
    );
  }

  return characteristics.join(' · ');
};

export type Listing =
  | ListingAutomaticType
  | NonNullable<ComparableListingsCmaListing>[number];

type ListingCardInfoProps = {
  listing: Listing;
  variant: 'horizontal' | 'vertical';
  showAgencyName?: boolean;
  showContactName?: boolean;
  showDaysOnMarket?: boolean;
  showOfflineSince?: boolean;
  positionDateBadge?: 'image' | 'inline';
};
const ListingCardInfo = ({
  listing,
  variant,
  showAgencyName = false,
  showContactName = false,
  showDaysOnMarket = true,
  showOfflineSince = true,
  positionDateBadge = 'image',
}: ListingCardInfoProps) => {
  const { getPropertyTypeLabel } = usePropertyTypes();
  const client = useApolloClient();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, locale, countryCode } = useLocale();
  const theme = useTheme();
  const [imageLoadError, setImageLoadError] = useState(false);
  const listingImages = listing.images?.map(image => image);
  const [primaryImage] = listingImages;
  const currencyToFormat = getCurrencyByCountryCode(countryCode);

  const handleClickCard = () => {
    const compositeListingId = `${listing.id}/${listing.state_id}`;
    const encodedListingId = btoa(compositeListingId);

    navigate({
      pathname: `${encodedListingId}`,
      search: searchParams.toString(),
    });
  };

  const agentName =
    listing.visit_contact_person ?? listing.agency_contact_person;

  return (
    <Stack direction={variant === 'vertical' ? 'column' : 'row'} height="100%">
      <Box
        onMouseEnter={() =>
          client.query(
            initialListingComparableDetails(
              listing.id,
              countryCode,
              listing.state_id ?? '',
            ),
          )
        }
        onClick={handleClickCard}
        sx={[
          {
            background: theme.palette.grey[200],
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          variant === 'vertical'
            ? {
                height: '200px',
                alignSelf: 'stretch',
              }
            : {
                width: { xs: '124px', sm: '160px' },
                minWidth: { xs: '124px', sm: '160px' },
              },
        ]}
      >
        <Box
          sx={[
            {
              position: 'absolute',
              zIndex: 1,
              alignSelf: 'flex-start',
            },
            variant === 'vertical'
              ? { top: '8px', left: '8px' }
              : {
                  bottom: { xs: '4px', sm: {} },
                  left: { xs: '4px', sm: '14px' },
                  top: { sm: '4px' },
                },
          ]}
        />
        {primaryImage != null && !imageLoadError ? (
          <Box sx={{ flexGrow: 1, height: 0 }}>
            <Image
              src={{
                bucketName: primaryImage.bucket_name ?? '',
                fileName: primaryImage.file_name ?? '',
              }}
              onError={() => setImageLoadError(true)}
            />
          </Box>
        ) : (
          <NoPhotosPlaceholder ratio={variant === 'vertical' ? 3 / 4 : 1} />
        )}
      </Box>
      <Stack
        onClick={handleClickCard}
        px={2}
        py={1}
        flexDirection="column"
        flexGrow={1}
        width="100%"
      >
        <Typography
          sx={[
            {
              lineHeight: 1.3,
              fontWeight: 'bold',
              fontSize: '1rem',
              whiteSpace: 'nowrap',
            },
          ]}
        >
          {getPropertyTypeLabel(listing.property_type)}
        </Typography>
        <Typography
          sx={{
            ...theme.typography.body2,
            color: theme.palette.grey['800'],
            fontSize: '0.8rem',
          }}
        >
          <Box>{getPropertyCharacteristics(listing, countryCode, t)}</Box>
          {listing.route || listing.street_number ? (
            <Box>
              {[
                [listing.route, listing.street_number]
                  .filter(Boolean)
                  .join(' ')
                  .trim(),
                [listing.postcode, listing.locality]
                  .filter(Boolean)
                  .join(' ')
                  .trim(),
              ]
                .filter(Boolean)
                .join(', ')}
            </Box>
          ) : (
            <Box>{listing.address}</Box>
          )}
        </Typography>
        <Box flexGrow={1} />
        <Box mt={1}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
            {listing.sale_price
              ? formatPrice(listing.sale_price, locale, currencyToFormat)
              : t('priceOnRequest')}
          </Typography>
          <Typography
            sx={{
              ...theme.typography.body2,
              color: theme.palette.grey['800'],
            }}
          >
            {listing.computed_price_per_sqm
              ? formatPrice(
                  listing.computed_price_per_sqm,
                  locale,
                  currencyToFormat,
                )
              : '- '}
            / m²
          </Typography>
        </Box>
        {(showContactName || showAgencyName) && (
          <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
            {showContactName && agentName && (
              <Chip icon={<Person />} label={agentName} />
            )}
            {showAgencyName && listing?.agency_name && (
              <Chip
                icon={<BusinessOutlinedIcon />}
                label={listing.agency_name}
                variant="outlined"
              />
            )}
          </Stack>
        )}

        {positionDateBadge === 'inline' ? (
          <Box
            display="flex"
            alignItems="flex-start"
            flexDirection="column"
            gap={1}
            mt={0.5}
          >
            {showDaysOnMarket && (
              <ListingStatusChip listing={listing} variant="duration" />
            )}
            {showOfflineSince && (
              <ListingStatusChip listing={listing} variant="status" />
            )}
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="flex-start"
            flexDirection="column"
            gap={1}
            position="absolute"
            top={8}
            left={8}
            sx={{
              zIndex: 1,
            }}
          >
            {showDaysOnMarket && (
              <ListingStatusChip
                listing={listing}
                variant="duration"
                shortLabels={true}
              />
            )}
            {showOfflineSince && (
              <ListingStatusChip
                listing={listing}
                variant="status"
                shortLabels={true}
              />
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default memo(ListingCardInfo);
